import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Edit,
  Trash2,
  MoreVertical,
  CheckCircle,
  User,
  Car,
  Clock,
} from 'lucide-react';
import airportImage from '../../assets/airportt.png';

const PAGE_SIZE = 12;

export default function FlexyRideMySeatListings() {
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const { t } = useTranslation();
  const [listings, setListings] = useState([]);
  const [requestsMeta, setRequestsMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [openMenuListingId, setOpenMenuListingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    // Listen for user's own seat listings
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'ride_listings'),
        where('mode', '==', 'ride'),
        where('type', '==', 'seat'),
        where('createdBy', '==', user.uid),
      ),
      snapshot => {
        const data = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
            return bTime - aTime;
          });
        setListings(data);
        setLoading(false);
      },
      error => {
        console.error('Error fetching listings:', error);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [user?.uid]);

  // Request counts & accepted/completed status for user's listings
  useEffect(() => {
    if (!user?.uid) return undefined;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'ride_requests'),
        where('listingOwnerId', '==', user.uid),
      ),
      snapshot => {
        const meta = {};
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const lid = data.listingId;
          if (!lid) return;
          if (!meta[lid]) {
            meta[lid] = { count: 0, hasAccepted: false, hasCompleted: false };
          }
          meta[lid].count += 1;
          if (data.status === 'accepted') {
            meta[lid].hasAccepted = true;
          }
          if (data.status === 'completed') {
            meta[lid].hasCompleted = true;
          }
        });
        setRequestsMeta(meta);
      },
      err => {
        console.error('Error fetching request meta:', err);
      },
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const handleEdit = listing => {
    navigate('/flexy-ride/create-seat', {
      state: {
        editMode: true,
        listingId: listing.id,
        listingData: listing,
      },
    });
  };

  const handleDelete = async listingId => {
    if (deletingId === listingId) return;
    if (!confirm(t('flexyRideMySeatListings.deleteConfirm'))) {
      return;
    }
    setDeletingId(listingId);
    try {
      await deleteDoc(doc(db, 'ride_listings', listingId));
      alert(t('flexyRideMySeatListings.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert(t('flexyRideMySeatListings.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(listings.length / PAGE_SIZE));
  const paginatedListings = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return listings.slice(startIndex, endIndex);
  }, [listings, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [listings.length, currentPage, totalPages]);

  const renderListingCard = listing => {
    const isCompleted =
      requestsMeta[listing.id]?.hasAccepted ||
      requestsMeta[listing.id]?.hasCompleted;

    return (
      <div
        className={`group relative rounded-2xl p-6 shadow-md border-2 transition-all duration-300 ${
          isCompleted
            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 hover:border-green-400 dark:hover:border-green-600'
            : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:shadow-xl hover:border-gray-300 dark:hover:border-slate-700'
        }`}
      >
        {/* Completed Badge */}
        {isCompleted && (
          <div className="mb-4 flex items-center justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-green-600 dark:bg-green-500 text-white dark:text-black rounded-full">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-bold">
                {t('flexyRideMySeatListings.completedBadge')}
              </span>
            </div>
          </div>
        )}

        {/* Route Header */}
        <div className="mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
                    {t('flexyRideMySeatListings.route')}
                  </p>
                  <p className="text-base font-bold text-gray-900 dark:text-white leading-tight truncate">
                    {listing.from || t('home.fromFallback')}{' '}
                    <span className="text-gray-400 dark:text-slate-500">→</span>{' '}
                    {listing.to || t('home.toFallback')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4 text-blue-500 dark:text-blue-400" />
              <span className="font-medium">
                {listing.date || t('flexyRideSeat.dateNotSet')} •{' '}
                {listing.time || t('flexyRideSeat.timeNotSet')}
              </span>
            </div>
            {listing.seats && (
              <div className="flex items-center gap-1.5">
                <User className="h-4 w-4 text-gray-500 dark:text-slate-400" />
                <span className="text-xs font-medium">
                  {t('flexyRideSeat.summarySeatsPriceValue', {
                    seats: listing.seats,
                    price: listing.price || '0',
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Info */}
        {listing.price && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-yellow-700 dark:text-yellow-300">
                {t('flexyRideMySeatListings.pricing')}
              </span>
              <span className="text-sm font-bold text-yellow-900 dark:text-yellow-200">
                {t('flexyRideMySeatListings.pricingValue', {
                  currency: listing.currency || 'CAD',
                  price: listing.price,
                  seats: listing.seats || 1,
                })}
              </span>
            </div>
          </div>
        )}

        {/* Car Info */}
        {(listing.carBrand || listing.carModel || listing.carYear) && (
          <div className="mb-4 flex items-center gap-2">
            <Car className="h-4 w-4 text-gray-500 dark:text-slate-400" />
            <span className="text-xs text-gray-600 dark:text-slate-400">
              {listing.carBrand || ''} {listing.carModel || ''}{' '}
              {listing.carYear ? `(${listing.carYear})` : ''}
            </span>
          </div>
        )}

        {/* Note */}
        {listing.note && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
              {t('flexyRideMySeatListings.note')}
            </p>
            <p className="text-sm text-gray-700 dark:text-slate-300 line-clamp-2">
              {listing.note}
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="mb-4 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-700 rounded-xl">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-gray-500 dark:text-slate-400 flex-shrink-0" />
            <span className="text-xs font-bold text-gray-900 dark:text-white">
              {requestsMeta[listing.id]?.count || 0}
            </span>
            <span className="text-xs text-gray-600 dark:text-slate-400">
              {t('flexyRideMySeatListings.requests')}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              navigate(`/flexy-ride/seat-listing-requests/${listing.id}`)
            }
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-black dark:bg-yellow-500 text-white dark:text-black rounded-xl text-sm font-semibold hover:bg-gray-900 dark:hover:bg-yellow-400 transition-all duration-200"
          >
            <MessageCircle className="h-4 w-4" />
            {t('flexyRideMySeatListings.viewRequests', {
              count: requestsMeta[listing.id]?.count || 0,
            })}
          </button>
          {!isCompleted && (
            <div className="relative">
              <button
                onClick={() =>
                  setOpenMenuListingId(
                    openMenuListingId === listing.id ? null : listing.id,
                  )
                }
                className="flex items-center justify-center p-2.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {openMenuListingId === listing.id && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setOpenMenuListingId(null)}
                  ></div>
                  <div className="absolute right-0 top-full mt-2 z-20 w-40 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <button
                      onClick={() => {
                        handleEdit(listing);
                        setOpenMenuListingId(null);
                      }}
                      disabled={deletingId === listing.id}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition disabled:opacity-50"
                    >
                      <Edit className="h-4 w-4" />
                    {t('common.edit')}
                    </button>
                    <button
                      onClick={() => {
                        handleDelete(listing.id);
                        setOpenMenuListingId(null);
                      }}
                      disabled={deletingId === listing.id}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-50"
                    >
                      {deletingId === listing.id ? (
                        <Clock className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      {t('common.delete')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const isLoading = initializing || loading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>
        <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800"
              >
                <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded-lg w-3/4 mb-4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-full mb-2 animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('flexyRideMySeatListings.loginToViewListings')}
            </p>
            <a
              href="/auth/login"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('common.goToLogin')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('flexyRideMySeatListings.title')}
              </h1>
              <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                {t('flexyRideMySeatListings.subtitle')}
              </p>
            </div>
            <div className="relative">
              <img
                src={airportImage}
                alt="Airport"
                className="w-full h-auto object-contain"
                style={{ maxHeight: '400px' }}
              />
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
        {listings.length === 0 ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-12 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <Car className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('flexyRideMySeatListings.emptyTitle')}
            </h2>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('flexyRideMySeatListings.emptyDescription')}
            </p>
            <a
              href="/flexy-ride/create-seat"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-6 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('flexyRideMySeatListings.createListing')}
            </a>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedListings.map(listing => (
                <div key={listing.id}>{renderListingCard(listing)}</div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.prev')}
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      currentPage === index + 1
                        ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
